import { useEffect, useState, useRef } from 'react';
import apiInstance from '../../api/api';
import { useSearchParams, Link } from "react-router-dom";
import AuthLayout from "../../components/AuthLayout/AuthLayout";

import { API_URL } from "../../configs";

const ReportTags = () => {
  const [searchParams, setSearchParams] = useSearchParams(); // Getting query parameters
  const [tags, setTags] = useState([]);     // Store tags and their counts
  const [totalCount, setTotalCount] = useState(0);  // Total number of tags
  const [error, setError] = useState(null);  // Handle any errors
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [page, setPage] = useState(initialPage);       // Track the current page
  const limit = 50;                          // Items per page
  const [tagFilter, setTagFilter] = useState('');   // Filter tags by name
  const [minCount, setMinCount] = useState('');     // Minimum resource count
  const [maxCount, setMaxCount] = useState('');     // Maximum resource count

  // Fetch tags based on current query params, tag name filter, and pagination
  // Move fetchTagsData to outer scope so it can be called after merge
  async function fetchTagsData() {
    try {
      const response = await apiInstance.get(`${API_URL}reports/tags`, {
        params: {
          tag_name: tagFilter,
          min_count: minCount || undefined,
          max_count: maxCount || undefined,
          limit: limit,
          offset: (page - 1) * limit
        }
      });

      if (response.status === 200) {
        setTags(response.data.data);  // Set the tags data from API
        setTotalCount(response.data.total || 0);  // Set total count from API response
      } else {
        setTags([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError("Failed to fetch tags. Please try again.");
    }
  }

  useEffect(() => {
    fetchTagsData();
  }, [tagFilter, minCount, maxCount, page]); // Refetch tags whenever filter or page changes

  useEffect(() => {
    // Sync page state with URL and filters
    const params = { page };
    if (tagFilter) params.tag = tagFilter;
    if (minCount) params.min = minCount;
    if (maxCount) params.max = maxCount;
    setSearchParams(params);
  }, [page, tagFilter, minCount, maxCount, setSearchParams]);

  // Handle page navigation
  const handleNextPage = () => {
    if (page < Math.ceil(totalCount / limit)) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };


  // Replace/Merge tag UI state
  const [showReplace, setShowReplace] = useState(false);
  const [replaceNew, setReplaceNew] = useState("");
  const [replaceMsg, setReplaceMsg] = useState("");
  const [replaceErr, setReplaceErr] = useState(null);

  // Merge tags state
  const [selectedTags, setSelectedTags] = useState([]); // array of tag names
  const [showMerge, setShowMerge] = useState(false);
  const mergeModalRef = useRef();
  const [primaryTag, setPrimaryTag] = useState("");
  const [mergeMsg, setMergeMsg] = useState("");
  const [mergeErr, setMergeErr] = useState(null);

  const handleTagSelect = (tagName) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const handleShowMerge = () => {
    setShowMerge(true);
    setMergeMsg("");
    setMergeErr(null);
    setPrimaryTag(selectedTags[0] || "");
    setTimeout(() => {
      if (mergeModalRef.current) {
        mergeModalRef.current.focus();
      }
    }, 100);
  };

  const handleMergeTagsSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMergeErr(null);
    setMergeMsg("");
    if (!primaryTag || selectedTags.length < 2) {
      setMergeErr("Select at least two tags and a primary tag.");
      return;
    }
    try {
      const response = await apiInstance.put(`${API_URL}resources/tags/replace`, {
        old_tags: selectedTags.filter(t => t !== primaryTag),
        new_tag: primaryTag,
      });
      setMergeMsg(response.data.message || "Tags merged successfully");
      setTimeout(() => setShowMerge(false), 500); // Close modal after short delay
      setSelectedTags([]);
      setPrimaryTag("");
      // Keep the last search value (do not change tagFilter)
      setPage(1);
      // Refetch tags after mergess
      fetchTagsData();
    } catch (err) {
      if (err.response) {
        setMergeErr(err.response.data.detail);
      } else {
        setMergeErr("An error occurred while merging tags.");
      }
    }
  };

  const handleTagFilterChange = (event) => {
    setTagFilter(event.target.value);
  };

  const handleMinCountChange = (event) => {
    setMinCount(event.target.value);
  };

  const handleMaxCountChange = (event) => {
    setMaxCount(event.target.value);
  };

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    fetchTagsData();
  };

  // Inline replace handler
  const handleReplaceTagSubmit = async (e) => {
    e.preventDefault();
    setReplaceErr(null);
    setReplaceMsg("");
    try {
      const response = await apiInstance.put(`${API_URL}resources/tags/replace`, {
        old_tag: tagFilter,
        new_tag: replaceNew,
      });
      setReplaceMsg(response.data.message || "Tag replaced successfully");
      setShowReplace(false);
      setReplaceNew("");
      setTagFilter("");
      setPage(1);
    } catch (err) {
      if (err.response) {
        setReplaceErr(err.response.data.detail);
      } else {
        setReplaceErr("An error occurred while trying to replace tags.");
      }
    }
  };

  return (
    <AuthLayout>
      <header className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fs-5">Tags Report</h2>
      </header>
      <div>
        {error && <div className="error">{error}</div>}
        {replaceMsg && <div className="alert alert-success my-2">{replaceMsg}</div>}
        {replaceErr && <div className="alert alert-danger my-2">{replaceErr}</div>}

      <div className="d-flex justify-content-start align-items-center mb-3">
        {/* Filter input, min/max count, and replace/merge on the same line */}
        <form className="d-flex align-items-center gap-2" style={{ maxWidth: 700 }} onSubmit={handleFilterSubmit}>
          <input
            type="text"
            className="form-control"
            placeholder="Filter by tag name"
            value={tagFilter}
            onChange={handleTagFilterChange}
            style={{ minWidth: 160 }}
          />
          <input
            type="number"
            className="form-control"
            placeholder="Min resources"
            value={minCount}
            onChange={handleMinCountChange}
            style={{ width: 110 }}
            min={0}
          />
          <input
            type="number"
            className="form-control"
            placeholder="Max resources"
            value={maxCount}
            onChange={handleMaxCountChange}
            style={{ width: 110 }}
            min={0}
          />
          <button className="btn btn-primary" type="submit">Apply</button>
          {!showReplace && (
            <button
              className="btn btn-light ms-2"
              title="Replace tag"
              onClick={() => { setShowReplace(true); setReplaceMsg(""); setReplaceErr(null); }}
              style={{ height: 38 }}
            >
              <i className="bi bi-arrow-left-right"></i>
            </button>
          )}
        </form>
        {showReplace && (
          <form className="d-flex align-items-center mb-3 ms-3" style={{ maxWidth: 600 }} onSubmit={handleReplaceTagSubmit}>
            {/* Hidden old tag input, value is tagFilter */}
            <input type="hidden" value={tagFilter} readOnly />
            <span className="me-2">{tagFilter || <span className="text-muted">(no tag)</span>}</span>
            <span className="mx-2">→</span>
            <input
              type="text"
              className="form-control me-2"
              placeholder="New tag"
              value={replaceNew}
              onChange={e => setReplaceNew(e.target.value)}
              required
              style={{ maxWidth: 180 }}
            />
            <button className="btn btn-primary me-2" type="submit" disabled={!tagFilter || !replaceNew}>Replace</button>
            <button className="btn btn-light" type="button" onClick={() => { setShowReplace(false); setReplaceNew(""); }}>Cancel</button>
          </form>
        )}
        <button
          className="btn btn-sm btn-secondary ms-2"
          disabled={selectedTags.length < 2}
          onClick={handleShowMerge}
          style={{ height: 38 }}
        >
          Merge Selected
        </button>
        {selectedTags.length > 0 && (
          <span className="text-muted ms-2">{selectedTags.length} selected</span>
        )}
      </div>
        {/* Display tags */}
        {tags.length > 0 ? (
          <>
            {/* Merge button moved above */}
            <ul className="my-4 list-unstyled" style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
              {tags.map((tag, index) => (
                <li key={index} className="mb-2 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={selectedTags.includes(tag._id)}
                    onChange={() => handleTagSelect(tag._id)}
                  />
                  <span
                    className="badge rounded bg-light small rounded me-2 text-dark fw-semibold fs-6 my-1 py-1 px-3 suggested-tag"
                  >{tag._id}</span> <span className="text-muted small">{tag.count} resources</span>
                </li>
              ))}
            </ul>
            {/* Merge Modal */}
            {showMerge && (
              <div
                className="modal fade show"
                tabIndex="-1"
                style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
                onClick={() => setShowMerge(false)}
                onKeyDown={e => { if (e.key === 'Escape') setShowMerge(false); }}
              >
                <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                  <div className="modal-content" ref={mergeModalRef}>
                    <form onSubmit={handleMergeTagsSubmit}>
                      <div className="modal-header">
                        <h5 className="modal-title">Merge Tags</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowMerge(false)}></button>
                      </div>
                      <div className="modal-body">
                        <div className="mb-2">Select primary tag (all others will be merged into this):</div>
                        <div className="mb-2">
                          {selectedTags.map(tag => (
                            <label key={tag} className="me-3">
                              <input
                                type="radio"
                                name="primaryTag"
                                value={tag}
                                checked={primaryTag === tag}
                                onChange={() => setPrimaryTag(tag)}
                                className="form-check-input me-1"
                              />
                              <span className="badge bg-secondary text-white">{tag}</span>
                            </label>
                          ))}
                        </div>
                        {mergeMsg && <div className="alert alert-success my-2">{mergeMsg}</div>}
                        {mergeErr && <div className="alert alert-danger my-2">{mergeErr}</div>}
                        <div className="text-muted mt-2" style={{ fontSize: '0.95em' }}>
                          All selected tags will be merged into the primary tag. Their resources will be updated, and old tags will be removed.
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-primary btn-sm me-2" type="submit" disabled={selectedTags.length < 2 || !primaryTag}>Merge</button>
                        <button className="btn btn-light btn-sm" type="button" onClick={() => setShowMerge(false)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <p>No tags found</p>
        )}

        {/* Pagination controls */}
        <div className="pagination d-flex align-items-center gap-2">
          <span className="badge bg-light text-muted">{totalCount} resource</span>
          <button className="btn btn-light btn-sm" onClick={handlePreviousPage} disabled={page === 1}>
            &lt;
          </button>
          {(() => {
            const totalPages = Math.max(1, Math.ceil(totalCount / limit));
            const pages = [];
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              pages.push(1);
              let start = Math.max(2, page - 1);
              let end = Math.min(totalPages - 1, page + 1);
              if (start > 2) pages.push('...');
              for (let i = start; i <= end; i++) {
                pages.push(i);
              }
              if (end < totalPages - 1) pages.push('...');
              pages.push(totalPages);
            }
            return pages.map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="px-1">...</span>
                : <button
                    key={p}
                    className={`btn btn-sm ${page === p ? 'btn-secondary' : 'btn-light'}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
            );
          })()}
          <button className="btn btn-light btn-sm" onClick={handleNextPage} disabled={page >= Math.ceil(totalCount / limit)}>
            &gt;
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ReportTags;
